import { DEFAULT_RESOLUTION_SETTINGS, RESOLUTION_META } from "../constants/flow.js";
import { normalizeExchangePolicy } from "../utils/exchangePolicy.js";

const SECRET_KEY = /token|password|secret|authorization|postintegration|accesstoken|refresh/i;

export function omitSecrets(value) {
  if (Array.isArray(value)) {
    return value.map(omitSecrets);
  }
  if (!value || typeof value !== "object") {
    return value;
  }
  return Object.entries(value).reduce((acc, [key, nested]) => {
    if (SECRET_KEY.test(key)) {
      return acc;
    }
    acc[key] = omitSecrets(nested);
    return acc;
  }, {});
}

export function asRefundMethod(value) {
  return value === "pix" || value === "both" ? value : "shopify";
}

export function mergeResolutionSettings(incoming) {
  const source = incoming && typeof incoming === "object" ? incoming : {};
  return {
    refund: {
      enabled: source.refund?.enabled ?? DEFAULT_RESOLUTION_SETTINGS.refund.enabled,
      method: asRefundMethod(source.refund?.method ?? DEFAULT_RESOLUTION_SETTINGS.refund.method),
    },
    exchange: {
      enabled: source.exchange?.enabled ?? DEFAULT_RESOLUTION_SETTINGS.exchange.enabled,
      policy: normalizeExchangePolicy(source.exchange?.policy),
    },
    storeCredit: {
      enabled: source.storeCredit?.enabled ?? DEFAULT_RESOLUTION_SETTINGS.storeCredit.enabled,
      bonusPercent: Number(source.storeCredit?.bonusPercent) || 0,
    },
    giftCard: {
      enabled: source.giftCard?.enabled ?? DEFAULT_RESOLUTION_SETTINGS.giftCard.enabled,
      bonusPercent: Number(source.giftCard?.bonusPercent) || 0,
      expiresInDays: Number(source.giftCard?.expiresInDays) || 0,
    },
    discountCode: {
      enabled: source.discountCode?.enabled ?? DEFAULT_RESOLUTION_SETTINGS.discountCode.enabled,
      bonusPercent: Number(source.discountCode?.bonusPercent) || 0,
      expiresInDays: Number(source.discountCode?.expiresInDays) || 0,
      prefix: source.discountCode?.prefix || "",
    },
    postCode: {
      timing: source.postCode?.timing === "after_approval" ? "after_approval" : "immediate",
    },
    attachments: {
      mode: ["optional", "required", "required_for_defect"].includes(source.attachments?.mode)
        ? source.attachments.mode
        : "off",
      maxFiles: Math.min(Math.max(Number(source.attachments?.maxFiles) || 3, 1), 10),
    },
  };
}

export function enabledResolutions(settings) {
  return Object.entries(mergeResolutionSettings(settings))
    .filter(([, value]) => value.enabled)
    .map(([type, value]) => ({
      type,
      ...value,
      ...RESOLUTION_META[type],
    }));
}

export function bonusLabel(option) {
  const percent = Number(option?.bonusPercent) || 0;
  if (percent <= 0) {
    return "";
  }
  return `+${percent}% de bônus`;
}

export function applyResolutionToProducts(products, resolutionType) {
  return (products || []).map((product) => {
    const isCredit = resolutionType === "storeCredit" || resolutionType === "giftCard" || resolutionType === "discountCode";
    const resolutionAction = resolutionType === "exchange" ? "Troca" : "Devolução";
    const isWarranty = product.action === "Garantia";
    return {
      ...product,
      action: isWarranty ? "Garantia" : resolutionAction,
      actionWarranty: isWarranty ? resolutionAction : undefined,
      credit: isCredit,
      newOrderVariantId: resolutionType === "exchange"
        ? (product.newOrderVariantId || product.variant_id)
        : null,
    };
  });
}

export function bonusAmount(price, percent) {
  return Number(price || 0) * (Number(percent || 0) / 100);
}

// Motivos em que o lojista pode exigir foto ("obrigatório só para defeito").
export const DEFECT_REASONS = ["DEFECTIVE", "NOT_AS_DESCRIBED", "WRONG_ITEM"];

export function attachmentsRequired(settings, products = []) {
  const mode = mergeResolutionSettings(settings).attachments.mode;
  if (mode === "required") return true;
  if (mode === "required_for_defect") return products.some((item) => DEFECT_REASONS.includes(item?.reason));
  return false;
}
